import {ActionIcon, createStyles, Tooltip} from '@mantine/core';
import React from 'react';

import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

import {LocalAssembly} from '../BulkAssemblyConstants';
import {getNextSlotIdx, getPreviousSlotIdx, slotHasSequences} from '../BulkAssemblyUtil';
import HomologyDetectionModal from './HomologyDetectionModal';

const DISABLED_MESSAGE =
  'To use automatic homology detection, you must select input sequences for both' +
  ' the current step and an adjacent step';

interface Props {
  assembly: LocalAssembly;
  slotIdx: number;
  changeBases: (bases: string) => void;
}

const useStyles = createStyles((theme) => ({
  tooltip: {
    width: `calc(12 * ${theme.spacing.lg})`,
  },
  iconButton: {
    padding: 0,
    position: 'absolute',
    right: theme.spacing.xxs,
    top: theme.spacing.xxs,
    cursor: 'pointer',
    '&[data-disabled]': {
      cursor: 'default',
      backgroundColor: 'transparent',
      border: 'none',
    },
  },
}));

const DetectHomologyButton = (props: Props): JSX.Element => {
  const {assembly, changeBases, slotIdx} = props;
  const {classes} = useStyles();

  const hasData =
    slotHasSequences(assembly, slotIdx) &&
    (slotHasSequences(assembly, getNextSlotIdx(assembly, slotIdx)) ||
      slotHasSequences(assembly, getPreviousSlotIdx(assembly, slotIdx)));

  const onClick = async (): Promise<void> => {
    if (!hasData) {
      return;
    }
    const homologyBases = await legacyRunModal(HomologyDetectionModal, {assembly, slotIdx});
    if (homologyBases) {
      changeBases(homologyBases);
    }
  };

  return (
    <Tooltip
      classNames={{tooltip: classes.tooltip}}
      events={{focus: true, hover: true, touch: true}}
      label={hasData ? 'Detect' : DISABLED_MESSAGE}
      multiline={true}
      withinPortal={true}
    >
      <ActionIcon
        aria-label="Detect"
        className={classes.iconButton}
        {...(!hasData ? {'data-disabled': true} : {})}
        size="xs"
        {...getDisabledTooltipButtonProps(!hasData, onClick, true)}
      >
        <BenchlingIcon dataTestAttrs={{component: 'DetectHomologyButton', element: 'root'}} icon="detect" />
      </ActionIcon>
    </Tooltip>
  );
};

export default DetectHomologyButton;
